import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, TextField } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle } from "@fortawesome/pro-light-svg-icons";

import type { ICompaniesRepository, IPartnersRepository } from "App/Modules/Repositories";
import type { ICompany, IPartnerSlim, IResponse } from "App/Modules/Models";

type AddCompanyProps = {
    mmCompaniesRepository: ICompaniesRepository;
    mmPartnersRepository: IPartnersRepository;
    onAddSucces?: (company: ICompany) => void;
    onClose: () => void;
};

export const AddCompanyDialog = (props: AddCompanyProps) => {
    const { t } = useTranslation();
    const [name, setName] = useState("");
    const [nameValid, setNameValid] = useState(true);
    const [corporateIdNumber, setCorporateIdNumber] = useState("");
    const [saveError, setSaveError] = useState(false);
    const [partners, setPartners] = useState<IResponse<IPartnerSlim[]>>(null);
    const [selectedParner, setSelectedPartner] = useState<IPartnerSlim>(null);
    const [partnerName, setPartnerName] = useState("");
    const [partnersLoading, setPartnersLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const validateName = () => {
        let valid = true;
        if (name.length === 0) {
            valid = false;
        }
        setNameValid(valid);
        return valid;
    };

    const validateForm = () => validateName();

    useEffect(() => {
        setPartnersLoading(true);
        const sub = props.mmPartnersRepository.getPartners().subscribe(
            (response) => {
                setPartners(response);
                if (response.data && response.data.length === 1) {
                    setSelectedPartner(response.data[0]);
                }
                setPartnersLoading(false);
            },
            (error) => {
                setPartners(error);
                setPartnersLoading(false);
            },
        );

        return () => {
            sub.unsubscribe();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const addCompany = () => {
        const company = { name, corporateIdNumber, partner: selectedParner } as ICompany;

        setSaving(true);
        props.mmCompaniesRepository.addCompany(company).subscribe(
            (response) => {
                if (response.data) {
                    if (props.onAddSucces) {
                        props.onAddSucces(response.data);
                    }
                    setSaving(false);
                    props.onClose();
                }
            },
            () => {
                setSaveError(true);
                setSaving(false);
            },
        );
    };

    return (
        <div className="card dialog">
            <div className="dialog__section dialog__section--full">
                <h1 className="title title--h1 title--no-spacing">
                    {`${t("Gui.Buttons.Add")} ${t("BeforeLogin.Company.Company")}`}
                </h1>
            </div>

            <div className="dialog__section dialog__section--full">
                <label className="caption" htmlFor="name">
                    {t("Entities.Common.Name")}
                </label>
                <TextField
                    id="name"
                    required
                    fullWidth
                    variant="outlined"
                    size="small"
                    type="text"
                    autoComplete="off"
                    value={name}
                    helperText={!nameValid ? t("Validation.Mixed.Required") : null}
                    error={!nameValid}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setName(e.target.value);
                    }}
                />
            </div>

            <div className="dialog__section dialog__section--full">
                <label className="caption" htmlFor="corporateIdNumber">
                    {t("Entities.Common.CorporateIdNumber")}
                </label>
                <TextField
                    id="corporateIdNumber"
                    required
                    fullWidth
                    variant="outlined"
                    size="small"
                    type="text"
                    autoComplete="off"
                    value={corporateIdNumber}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setCorporateIdNumber(e.target.value);
                    }}
                />
            </div>

            {partners?.data?.length > 1 ? (
                <div className="dialog__section dialog__section--full">
                    <label className="caption" htmlFor="manufacturer">
                        {t("Entities.Partner.Partner")}
                    </label>
                    <Autocomplete
                        id="partner"
                        options={partners?.data || ([] as IPartnerSlim[])}
                        getOptionLabel={(p) => p.name}
                        value={selectedParner}
                        inputValue={partnerName}
                        loading={partnersLoading}
                        onChange={(e, value: IPartnerSlim) => setSelectedPartner(value)}
                        onInputChange={(e, value) => setPartnerName(value)}
                        renderInput={(params) => (
                            <TextField {...params} variant="outlined" size="small" data-testid="select-partner" />
                        )}
                    />
                </div>
            ) : null}

            {saveError ? (
                <div className="dialog__section dialog__section--full dialog__section--noscroll">
                    <div className="notice notice--critical">
                        <FontAwesomeIcon className="notice__icon" fixedWidth icon={faExclamationTriangle} />
                        <p className="caption">{t("Error.ErrorSaving")}</p>
                    </div>
                </div>
            ) : null}

            <div className="dialog__section dialog__section--buttons">
                <Button color="default" onClick={() => props.onClose()}>
                    {t("Gui.Buttons.Cancel")}
                </Button>

                <Button
                    data-testid="add-button"
                    disabled={saving || partnersLoading}
                    color="primary"
                    variant="contained"
                    onClick={() => {
                        if (validateForm()) {
                            addCompany();
                        }
                    }}
                >
                    {t("Gui.Buttons.Add")}
                </Button>
            </div>
        </div>
    );
};
